import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { CalendarCheck, ClipboardX, ChevronDown, ChevronUp, Send } from 'lucide-react';
import api from '../../services/api';

const COLORS = {
  confirmed: ['var(--success-50, #ecfdf5)', 'var(--success-700, #047857)'],
  declined: ['var(--danger-50, #fef2f2)', 'var(--danger-700, #b91c1c)'],
  pending: ['var(--warning-50, #fffbeb)', 'var(--warning-700, #b45309)'],
  notSent: ['var(--color-surface-alt, #f3f4f6)', 'var(--color-text-secondary, #4b5563)'],
  notDelivered: ['var(--danger-50, #fef2f2)', 'var(--danger-700, #b91c1c)'],
};

const chip = (k) => ({
  display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 8px', borderRadius: 999, fontSize: '0.75rem', fontWeight: 700,
  background: COLORS[k][0], color: COLORS[k][1], whiteSpace: 'nowrap',
});

/**
 * Sport dashboard (docs/sport-center-v2-plan.md §5, §7): who confirmed the
 * upcoming sessions, and past sessions still missing attendance — without
 * attendance, a client who didn't confirm is not charged.
 */
export default function SportSessionsCard({ refreshKey = 0 }) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(null);
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState(null);

  const load = useCallback(() => {
    api.get('/sport/dashboard')
      .then((r) => setData(r?.data || r || null))
      .catch(() => setData(null));
  }, []);
  useEffect(() => { load(); }, [load, refreshKey]);

  // One WhatsApp per session to its coaches, with a fresh attendance link, for
  // every session of the last two weeks still missing attendance.
  const sendLinks = async () => {
    if (!window.confirm(t('sport.sessionsCard.sendLinksConfirm', { count: (data?.missingAttendance || []).length }))) return;
    setSending(true); setSendResult(null);
    try {
      const r = await api.post('/sport/attendance-links/send');
      const d = r?.data || r || {};
      const noPhone = (d.results || []).filter((x) => x.status === 'no_coach_phone').length;
      setSendResult(t('sport.sessionsCard.sendLinksDone', { sent: d.sent || 0, noPhone }));
    } catch (e) {
      setSendResult(e?.message || t('common.error'));
    }
    setSending(false);
  };

  if (!data) return null;
  const upcoming = data.upcoming || [];
  const missing = data.missingAttendance || [];
  if (!upcoming.length && !missing.length) return null;

  const dateFmt = (key, time) => {
    try {
      const d = new Intl.DateTimeFormat(i18n.language, { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC' }).format(new Date(`${key}T00:00:00Z`));
      return time ? `${d} · ${time}` : d;
    } catch { return `${key} ${time || ''}`; }
  };
  const when = (iso) => {
    try { return new Intl.DateTimeFormat(i18n.language, { weekday: 'short', hour: '2-digit', minute: '2-digit', hourCycle: 'h23', timeZone: 'Asia/Jerusalem' }).format(new Date(iso)); } catch { return ''; }
  };
  const row = {
    display: 'grid', gap: 6, padding: '0.6rem 0.75rem', borderRadius: 12, border: '1px solid var(--color-border, #e5e7eb)', background: 'var(--color-surface, #fff)',
  };

  return (
    <section className="sport-intakes" data-testid="sport-sessions-card" style={{ display: 'grid', gap: '1rem' }}>
      {upcoming.length > 0 && (
        <div style={{ display: 'grid', gap: 8 }}>
          <h2 className="learning-dashboard__section-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <CalendarCheck size={18} /> {t('sport.sessionsCard.upcoming')}
          </h2>
          {upcoming.map((u) => {
            const isOpen = open === u.lessonId;
            return (
              <div key={u.lessonId} style={row} data-testid="sport-upcoming-session">
                <button
                  type="button"
                  onClick={() => setOpen(isOpen ? null : u.lessonId)}
                  aria-expanded={isOpen}
                  style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'none', border: 'none', boxShadow: 'none', padding: 0, minHeight: 44, cursor: 'pointer', color: 'var(--color-text-primary)', textAlign: 'start', width: '100%' }}
                >
                  <span style={{ flex: 1, minWidth: 0 }}>
                    <span style={{ fontWeight: 700, display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{u.groupName || u.studentName || t('sport.sessionsCard.privateSession')}</span>
                    <span style={{ fontSize: '0.8rem', color: 'var(--color-text-tertiary)' }}>
                      {dateFmt(u.date, u.startTime)}
                      {!u.windowOpen && u.sendsAt && new Date(u.sendsAt) > new Date() ? ` · ${t('sport.sessionsCard.sendsAt', { when: when(u.sendsAt) })}` : ''}
                    </span>
                  </span>
                  {isOpen ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                </button>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {['confirmed', 'declined', 'pending', 'notDelivered', 'notSent'].filter((k) => u.counts[k]).map((k) => (
                    <span key={k} style={chip(k)}>{t(`sport.sessionsCard.status.${k}`)} · {u.counts[k]}</span>
                  ))}
                </div>
                {isOpen && (
                  <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 4 }}>
                    {u.people.map((p) => (
                      <li key={p.studentId} style={{ display: 'flex', justifyContent: 'space-between', gap: 8, fontSize: '0.85rem', alignItems: 'center', minHeight: 32 }}>
                        <button type="button" onClick={() => navigate(`/students/${p.studentId}`)} style={{ background: 'none', border: 'none', boxShadow: 'none', padding: 0, cursor: 'pointer', color: 'var(--color-text-primary)', textAlign: 'start', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.name || '—'}</button>
                        <span style={chip(p.status)}>{t(`sport.sessionsCard.status.${p.status}`)}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            );
          })}
        </div>
      )}

      {missing.length > 0 && (
        <div style={{ display: 'grid', gap: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', justifyContent: 'space-between' }}>
            <h2 className="learning-dashboard__section-title" style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--danger-700, #b91c1c)', margin: 0 }}>
              <ClipboardX size={18} /> {t('sport.sessionsCard.missingTitle', { count: missing.length })}
            </h2>
            <button type="button" className="btn btn-secondary" onClick={sendLinks} disabled={sending} data-testid="send-attendance-links"
              style={{ minHeight: 44, display: 'inline-flex', alignItems: 'center', gap: 6, whiteSpace: 'normal' }}>
              <Send size={16} /> {sending ? t('sport.sessionsCard.sendingLinks') : t('sport.sessionsCard.sendLinks')}
            </button>
          </div>
          {sendResult && <p role="status" data-testid="send-attendance-links-result" style={{ margin: 0, fontSize: '0.85rem', color: 'var(--color-text-secondary)' }}>{sendResult}</p>}
          <p style={{ margin: 0, fontSize: '0.8rem', color: 'var(--color-text-secondary)' }}>{t('sport.sessionsCard.missingHint')}</p>
          {missing.map((m) => (
            <div key={m.lessonId} style={{ ...row, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }} data-testid="sport-missing-attendance">
              <span style={{ flex: 1, minWidth: 160 }}>
                <span style={{ fontWeight: 700, display: 'block' }}>{m.groupName || m.studentName || t('sport.sessionsCard.privateSession')}</span>
                <span style={{ fontSize: '0.8rem', color: 'var(--color-text-tertiary)' }}>{dateFmt(m.date, m.startTime)} · {t('sport.sessionsCard.missingCount', { missing: m.missing, total: m.total })}</span>
              </span>
              <button type="button" className="btn btn-primary" style={{ minHeight: 44 }} onClick={() => navigate(`/lessons/${m.lessonId}/attendance`)}>
                {t('sport.sessionsCard.markNow')}
              </button>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
